import React, { useState } from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { List, ListItem, Sup, Text } from "@onnit-js/ui/components/text";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import { Img, Picture } from "@onnit-js/ui/components/image";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import usePageData from "../../hooks/usePageData";
import SupplementFacts from "../../components/common/SupplementFacts";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import config from "../../config/config";

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();
    // Used to set current products supplement facts image.
    const [currentPID, setCurrentPID] = useState<number | undefined>();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMG_URL = `${onnitContext.url.cdn.static_images}/product-page/mct-oil`;
    const { IMG_PARAMS } = config;
    const mainColor = "#4DC1B8";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop
                    onProductChange={(product) => {
                        setCurrentPID(product.id);
                    }}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={1943}
                            height={873}
                            alt="Bottle of MCT oil with coconuts in the background"
                            src={[
                                `${IMG_URL}/2024-MCT-Wpage-Gallery-1-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2024-MCT-Wpage-Gallery-1-1940x873.png${IMG_PARAMS}`,
                            ]}
                        />
                        <Img
                            alt="bunch of coconuts"
                            src={`${IMG_URL}/2024-MCT-Wpage-Gallery-2-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                        <Img
                            alt="man running on the beach"
                            src={`${IMG_URL}/2024-MCT-Wpage-Gallery-4-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <Box bg="#161616" mt={[0, 0, 6, 8]} py={[6, 6, 6, 8]}>
                    <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                        <Box gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]} px={4}>
                            <Text as="h2" color="grays.3" typeStyle="text02" fontWeight="regular">
                                MCT Oil
                            </Text>
                        </Box>
                    </TwelveColumn>
                    <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                        <Box
                            gridColumn={["7 / span 6", "7 / span 6", "7 / span 6", "7 / span 5"]}
                            px={4}
                            mb={[6, 6, 0]}
                        >
                            <Img
                                src={`${IMG_URL}/2024-MCT-Wpage-Product-Hero-1920x1080.png${IMG_PARAMS}`}
                                alt="detailed image of a krill"
                            />
                        </Box>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 5"]}
                            display={["block", "block", "flex"]}
                            alignItems="center"
                            px={4}
                        >
                            <Text
                                display={["block", "block", "none"]}
                                as="h2"
                                color="grays.3"
                                typeStyle="text02"
                                fontWeight="regular"
                                my={3}
                            >
                                MCT Oil
                            </Text>

                            <Text as="h3" typeStyle="title01" color={mainColor} mb={2}>
                                The ketogenic kickstarter
                            </Text>
                        </Box>
                    </TwelveColumn>

                    <TwelveColumn display={["block", "block", "grid"]}>
                        <Box
                            gridColumn={["1 / span 6", "1 / span 6", "1 / span 6", "2 / span 7"]}
                            px={4}
                            mb={[6, 6, 6, 8]}
                        >
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                Medium-chain triglycerides (MCTs) are a type of fat. Looking at them chemically, they
                                have shorter fatty-acid chains compared to long- chain fats (the common fats typically
                                found in our diets), and that means they can work more quickly, providing a fast source
                                of energy. <Sup />
                            </Text>
                            <Text as="p" color="white" typeStyle="text02" mb={4} fontWeight="regular">
                                Coconut oil is one of the richest natural sources of MCTs. Onnit’s MCT Oil is sourced
                                from coconut oil but is more concentrated, providing more MCTs and a greater percentage
                                of caprylic acid, which helps to form ketone bodies. This is good news for followers of
                                a ketogenic diet, as MCT Oil is better than coconut oil for getting into and maintaining
                                ketosis.
                                <Sup />
                            </Text>
                        </Box>
                        <Box gridColumn={["2 / span 10", "2 / span 10", "2 / span 10", "3 / span 10"]} px={4}>
                            <Text as="h3" typeStyle="title01" color={mainColor} mb={4}>
                                MCT Oil is for:
                            </Text>
                            <List>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    Lower-carb and ketogenic dieters
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular" mb={3}>
                                    People who are following a strict diet and feel low energy
                                </ListItem>
                                <ListItem typeStyle="text02" color="white" fontWeight="regular">
                                    Those who want a concentrated source of MCTs
                                </ListItem>
                            </List>
                        </Box>
                    </TwelveColumn>
                </Box>

                <ImageContentPortrait
                    py={[6, 6, 6, 8]}
                    reverse
                    image={(
                        <Img
                            alt="Derived form NON-GMO coconut"
                            placeholder={[464, 600]}
                            src={`${IMG_URL}/2024-MCT-Wpage-How-1.png${IMG_PARAMS}`}
                            width={926}
                            height={1200}
                        />
                      )}
                    content={(
                        <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                            <Text as="h2" typeStyle="title02" mb={[4, 4, 6]}>
                                How does MCT Oil Work?
                            </Text>
                            <Text as="p" typeStyle="text02" fontWeight="regular" color="grays.5">
                                When MCTs are consumed, they go straight to the liver, where they’re rapidly digested
                                and absorbed, or, if you’re on a ketogenic diet, they provide the building blocks for
                                ketones, which the body uses in place of carbohydrate for fuel. This fast turnaround
                                makes supplementing with MCTs for the keto dieter what hitting the nitro button is to a
                                street drag racer!
                                <Sup variant="dagger" />
                            </Text>
                        </Box>
                      )}
                />

                <UsageAndDisclaimers supplementWarning supplementFacts={<SupplementFacts currentPID={currentPID} />} />
            </>
        </ThemeProvider>
    );
}
